import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Slider,
  Button,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BusinessIcon from '@mui/icons-material/Business';
import WebIcon from '@mui/icons-material/Web';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

const projectTypes = [
  {
    id: 'basic',
    title: 'Basic Website',
    icon: <WebIcon sx={{ fontSize: 40 }} />,
    basePrice: 1500,
    features: ['Responsive Design', 'SEO Optimierung', 'Kontaktformular'],
  },
  {
    id: 'shop',
    title: 'Online Shop',
    icon: <ShoppingCartIcon sx={{ fontSize: 40 }} />,
    basePrice: 3000,
    features: ['Produktkatalog', 'Warenkorb', 'Zahlungsabwicklung'],
  },
  {
    id: 'business',
    title: 'Business Portal',
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    basePrice: 5000,
    features: ['Benutzerverwaltung', 'Dashboard', 'Analytik'],
  },
];

const additionalFeatures = [
  { id: 'blog', title: 'Blog System', price: 800 },
  { id: 'multilingual', title: 'Mehrsprachigkeit', price: 1200 },
  { id: 'newsletter', title: 'Newsletter Integration', price: 500 },
  { id: 'analytics', title: 'Erweiterte Analytik', price: 600 },
  { id: 'seo', title: 'SEO Paket', price: 900 },
  { id: 'security', title: 'Sicherheitspaket', price: 700 },
];

const CalculatorSection: React.FC = () => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [pages, setPages] = useState<number>(5);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
  };

  const handleFeatureToggle = (featureId: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const calculateTotal = () => {
    if (!selectedType) return 0;
    
    const basePrice = projectTypes.find((type) => type.id === selectedType)?.basePrice || 0;
    const featuresPrice = selectedFeatures.reduce(
      (sum, featureId) =>
        sum + (additionalFeatures.find((f) => f.id === featureId)?.price || 0),
      0
    );
    const pagesPrice = Math.max(0, pages - 5) * 200;

    return basePrice + featuresPrice + pagesPrice;
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          Projekt Kalkulator
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h5" gutterBottom>
                1. Wählen Sie Ihren Projekttyp
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {projectTypes.map((type) => (
                  <Grid item xs={12} sm={4} key={type.id}>
                    <Paper
                      elevation={0}
                      onClick={() => handleTypeSelect(type.id)}
                      sx={{
                        p: 3,
                        border: `2px solid ${
                          selectedType === type.id
                            ? theme.palette.primary.main
                            : theme.palette.divider
                        }`,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor:
                          selectedType === type.id
                            ? 'action.selected'
                            : 'background.paper',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ color: 'primary.main', mb: 2 }}>
                          {type.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom>
                          {type.title}
                        </Typography>
                        <Typography
                          variant="h5"
                          color="primary"
                          sx={{ mb: 2 }}
                        >
                          ab {type.basePrice}€
                        </Typography>
                        <Box>
                          {type.features.map((feature) => (
                            <Typography
                              key={feature}
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ fontSize: 16, color: 'success.main' }}
                              />
                              {feature}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {selectedType && (
                <>
                  <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                    2. Anzahl der Seiten
                  </Typography>
                  <Box sx={{ px: 2, mb: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <IconButton
                        onClick={() => setPages(Math.max(1, pages - 1))}
                        color="primary"
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography variant="h6">{pages} Seiten</Typography>
                      <IconButton
                        onClick={() => setPages(pages + 1)}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Slider
                      value={pages}
                      onChange={(_, value) => setPages(value as number)}
                      min={1}
                      max={20}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 5, label: '5' },
                        { value: 10, label: '10' },
                        { value: 20, label: '20' },
                      ]}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Die ersten 5 Seiten sind im Basispreis enthalten. Jede
                      weitere Seite kostet 200€.
                    </Typography>
                  </Box>

                  <Typography variant="h5" gutterBottom>
                    3. Zusätzliche Features
                  </Typography>
                  <Box sx={{ mb: 4 }}>
                    {additionalFeatures.map((feature) => (
                      <Chip
                        key={feature.id}
                        label={`${feature.title} (+${feature.price}€)`}
                        onClick={() => handleFeatureToggle(feature.id)}
                        color={
                          selectedFeatures.includes(feature.id)
                            ? 'primary'
                            : 'default'
                        }
                        variant={
                          selectedFeatures.includes(feature.id)
                            ? 'filled'
                            : 'outlined'
                        }
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                border: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 24,
              }}
            >
              <Typography variant="h5" gutterBottom>
                Kostenübersicht
              </Typography>

              {selectedType ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Projekttyp:{' '}
                      {projectTypes.find((t) => t.id === selectedType)?.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Seiten: {pages}
                    </Typography>
                    {selectedFeatures.length > 0 && (
                      <Typography variant="body1">
                        Zusätzliche Features:{' '}
                        {selectedFeatures
                          .map(
                            (id) =>
                              additionalFeatures.find((f) => f.id === id)?.title
                          )
                          .join(', ')}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="h4" color="primary" gutterBottom>
                    Gesamtpreis: {calculateTotal()}€
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    href="/contact"
                    sx={{ mt: 2 }}
                  >
                    Projekt anfragen
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary">
                  Bitte wählen Sie einen Projekttyp aus, um eine Kostenübersicht zu
                  erhalten.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CalculatorSection;
